import { NextLink } from '@common/NextLink/NextLink';
import { WidenImage } from '@common/Widen/WidenImage';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import { displayPrice } from '@libs/product-price-utils';
import type { WidenMediaElement } from '@libs/widen-utils';
import { Badge, BadgeSize } from '@common/Badge/Badge';

export interface ProductBoxProps {
  sku: string;
  title: string;
  subtitle?: string;
  image?: WidenMediaElement;
  pricePerUnit?: number; // in cent
  currencyCode?: string;
  showPrice?: boolean;
  option?: boolean; // the product box will be used as an option in a select list
  linkTargetOverride?: string;
  externalLink?: boolean;
  recommended?: boolean;
}

export const ProductBox = ({
  sku,
  title,
  subtitle,
  image,
  pricePerUnit, // in cent
  currencyCode,
  option, // the product box will be used as an option in a select list
  linkTargetOverride,
  externalLink,
  recommended,
  showPrice,
}: ProductBoxProps) => {
  const isExternalLink = externalLink || false;
  const linkTarget = linkTargetOverride || `/product/${sku}`;
  const { t, i18n } = useTranslation(['category/productTeaserPriceBox', 'category/productTeaser']);
  const canShowPrice = pricePerUnit && pricePerUnit > 0;
  const productBox = (
    <div className={classNames('flex flex-row mt-1 py-2 px-4', !option && 'border rounded border-gray-200')}>
      {image && (
        <div className={classNames('xl:p-3 pr-1', option && 'border rounded border-gray-200')}>
          <span className={classNames('align-middle')}>
            <WidenImage
              data-testid="product-box-image"
              width={50}
              height={50}
              layout="fixed"
              alt={image.description ?? title}
              shortCode={image.shortCode}
              filename={image.filename}
            />
          </span>
        </div>
      )}
      <div className={classNames('xl:text-sm lg:text-xs sm:text-xs xl:px-2', recommended ? '' : 'xl:py-5')}>
        {recommended && (
          <span className={classNames('block', 'h-9')}>
            <Badge
              size={BadgeSize.ExtraSmall}
              text={t('PRODUCT_OVERVIEW.PRODUCT_RECOMMENDATION', { ns: 'category/productTeaser' })}
            />
          </span>
        )}
        <span className="font-bold">{title}</span>
        {subtitle && <span className="block">{subtitle}</span>}
        {showPrice &&
          ((canShowPrice && (
            <div className="my-1.5 block">{`${displayPrice(pricePerUnit, currencyCode, i18n.language, true)} / ${t(
              'PRODUCT_OVERVIEW.PRICE_BOX.LENGTH_AND_PIECE.PIECE_ABBREVIATION'
            )}`}</div>
          )) || <span className="my-1.5 block">{t('PRODUCT_OVERVIEW.PRICE_BOX.PRICE_ON_REQUEST')}</span>)}
      </div>
    </div>
  );
  if (option) {
    return productBox;
  }
  if (isExternalLink) {
    return (
      <a href={linkTarget} target="_blank" rel="noreferrer" data-testid="product-box-link" aria-label={title}>
        {productBox}
      </a>
    );
  }
  return (
    <NextLink data-testid="product-box-nextjs-link" href={linkTarget} aria-label={title}>
      {productBox}
    </NextLink>
  );
};
